import { useEffect, useState } from "react";
import api from "@/lib/api";
import { User, Search } from "lucide-react";

const RISK = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  routine: "bg-emerald-100 text-emerald-700",
};

export function usePatients() {
  const [patients, setPatients] = useState([]);
  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}); }, []);
  return patients;
}

function matches(p, q) {
  return `${p.name} ${p.cliniko_id} ${p.diagnosis || ""}`.toLowerCase().includes(q.toLowerCase());
}

// Debounced live Cliniko search (falls back to cache on the server side).
function useLiveSearch(q) {
  const [remote, setRemote] = useState([]);
  useEffect(() => {
    const term = (q || "").trim();
    if (!term) { setRemote([]); return; }
    let ignore = false;
    const t = setTimeout(() => {
      api.get("/patients/search", { params: { q: term } })
        .then((r) => { if (!ignore) setRemote(r.data || []); })
        .catch(() => {});
    }, 250);
    return () => { ignore = true; clearTimeout(t); };
  }, [q]);
  return remote;
}

// Merge cached matches with live Cliniko results, de-duped by cliniko_id.
function mergeResults(cacheList, remote, cap = 60) {
  const map = {};
  [...cacheList, ...remote].forEach((p) => { if (p && p.cliniko_id) map[p.cliniko_id] = p; });
  return Object.values(map).slice(0, cap);
}

// Cache + live Cliniko search merged — works even when the local cache is empty/unsynced.
export function useHybridPatients(q) {
  const cache = usePatients();
  const remote = useLiveSearch(q);
  const term = (q || "").trim();
  const local = term ? cache.filter((p) => matches(p, q)) : cache;
  return mergeResults(local, remote, term ? 80 : 2000);
}

export default function PatientPicker({ value, onChange, testid = "patient-picker" }) {
  const patients = usePatients();
  const [q, setQ] = useState("");
  const remote = useLiveSearch(q);
  const localMatches = q.trim() ? patients.filter((p) => matches(p, q)) : patients;
  const list = mergeResults(localMatches, remote, 80);
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} placeholder="Search all Cliniko patients..."
          className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500" />
      </div>
      <div className="max-h-[60vh] space-y-1.5 overflow-auto pr-0.5">
        {list.map((p) => {
          const active = value === p.cliniko_id;
          return (
            <button key={p.cliniko_id} onClick={() => onChange(p.cliniko_id)} data-testid={`patient-option-${p.cliniko_id}`}
              className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all ${active ? "border-teal-500 bg-teal-50 ring-1 ring-teal-500/30" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-full ${active ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                <User className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-slate-800">{p.name}</div>
                <div className="truncate text-xs text-slate-500">{p.diagnosis || "—"}</div>
              </div>
              <div className="flex-shrink-0 font-mono text-[10px] text-slate-400">{p.cliniko_id}</div>
            </button>
          );
        })}
        {list.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No patients match "{q}"</div>}
      </div>
    </div>
  );
}

export function PatientSearchSelect({ value, onChange, placeholder = "Search patient...", testid = "patient-search-select", allowAll = false }) {
  const patients = usePatients();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [pickedLabels, setPickedLabels] = useState({});
  const remote = useLiveSearch(open ? q : "");

  const byId = {};
  [...patients, ...remote].forEach((p) => { if (p && p.cliniko_id) byId[p.cliniko_id] = p; });
  const selected = byId[value];
  const label = value === "all"
    ? "All patients"
    : (selected ? `${selected.name} · ${selected.cliniko_id}` : (pickedLabels[value] || ""));

  const localMatches = q.trim() ? patients.filter((p) => matches(p, q)) : patients;
  const list = mergeResults(localMatches, remote, 60);

  const pick = (p) => {
    setPickedLabels((l) => ({ ...l, [p.cliniko_id]: `${p.name} · ${p.cliniko_id}` }));
    onChange(p.cliniko_id);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid={testid}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
        value={open ? q : label} placeholder={placeholder}
        onFocus={() => { setOpen(true); setQ(""); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => setQ(e.target.value)}
        data-testid={`${testid}-input`}
      />
      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
          {allowAll && (
            <button onMouseDown={() => { onChange("all"); setOpen(false); }} className="flex w-full rounded-lg px-2.5 py-2 text-left text-sm font-medium text-slate-600 hover:bg-teal-50">All patients</button>
          )}
          {list.map((p) => (
            <button key={p.cliniko_id} onMouseDown={() => pick(p)} data-testid={`${testid}-opt-${p.cliniko_id}`}
              className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-teal-50">
              <span className="truncate"><span className="font-medium text-slate-800">{p.name}</span> <span className="text-xs text-slate-400">{p.diagnosis}</span></span>
              <span className="flex-shrink-0 font-mono text-[10px] text-slate-400">{p.cliniko_id}</span>
            </button>
          ))}
          {list.length === 0 && <div className="px-3 py-3 text-center text-xs text-slate-400">{q.trim() ? "No patients match" : "Type to search Cliniko"}</div>}
        </div>
      )}
    </div>
  );
}

export { RISK };
